import type { ReadonlyVec2 } from "@core/types";

/** Нормализованное состояние ввода за один тик. */
export interface InputState {
  readonly axis: ReadonlyVec2; // нормализованный, длина <= 1
  readonly glowPressed: boolean;
}

/** Источник ввода — опрашивается каждый тик. */
export interface InputSource {
  poll(): InputState;
  dispose(): void;
}

/** Нулевое состояние ввода. */
const ZERO_INPUT: InputState = { axis: { x: 0, y: 0 }, glowPressed: false };
export { ZERO_INPUT };

/** Клавиатурный источник. Слушает window keydown/keyup. */
export class KeyboardInputSource implements InputSource {
  private readonly keys = new Set<string>();
  private glow = false;

  private readonly onDown = (e: KeyboardEvent): void => {
    const key = e.key.toLowerCase();
    if (["arrowleft", "arrowright", "arrowup", "arrowdown", " ", "spacebar"].includes(key)) {
      e.preventDefault();
    }
    if (key === " " || key === "spacebar") {
      this.glow = true;
      return;
    }
    this.keys.add(key);
  };

  private readonly onUp = (e: KeyboardEvent): void => {
    const key = e.key.toLowerCase();
    if (key === " " || key === "spacebar") {
      this.glow = false;
      return;
    }
    this.keys.delete(key);
  };

  constructor() {
    window.addEventListener("keydown", this.onDown);
    window.addEventListener("keyup", this.onUp);
  }

  /** Опрашивает текущее состояние клавиш. */
  poll(): InputState {
    let x = 0;
    let y = 0;
    if (this.keys.has("arrowleft") || this.keys.has("a")) x -= 1;
    if (this.keys.has("arrowright") || this.keys.has("d")) x += 1;
    if (this.keys.has("arrowup") || this.keys.has("w")) y -= 1;
    if (this.keys.has("arrowdown") || this.keys.has("s")) y += 1;
    return { axis: { x, y }, glowPressed: this.glow };
  }

  /** Отписывается от window-событий и очищает состояние. */
  dispose(): void {
    window.removeEventListener("keydown", this.onDown);
    window.removeEventListener("keyup", this.onUp);
    this.keys.clear();
    this.glow = false;
  }
}

/** Joystick на HTMLElement. Принимает элементы через DI — не querySelector. */
export class JoystickInputSource implements InputSource {
  private axis: ReadonlyVec2 = { x: 0, y: 0 };
  private active = false;
  private pointerId: number | null = null;

  private readonly onDown = (e: PointerEvent): void => {
    this.active = true;
    this.pointerId = e.pointerId;
    this.base.setPointerCapture(e.pointerId);
    this.updateFromEvent(e);
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (this.active) this.updateFromEvent(e);
  };

  private readonly onUp = (e: PointerEvent): void => {
    if (this.pointerId !== null && e.pointerId !== this.pointerId) return;
    this.active = false;
    this.pointerId = null;
    this.axis = { x: 0, y: 0 };
    this.knob.style.transform = "translate(-50%, -50%)";
  };

  constructor(
    private readonly base: HTMLElement,
    private readonly knob: HTMLElement,
  ) {
    this.base.addEventListener("pointerdown", this.onDown);
    this.base.addEventListener("pointermove", this.onMove);
    this.base.addEventListener("pointerup", this.onUp);
    this.base.addEventListener("pointercancel", this.onUp);
  }

  private updateFromEvent(e: PointerEvent): void {
    const rect = this.base.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const max = rect.width * 0.33;
    const length = Math.hypot(dx, dy);
    const scale = length > max ? max / length : 1;
    const knobX = dx * scale;
    const knobY = dy * scale;
    this.knob.style.transform = `translate(calc(-50% + ${knobX}px), calc(-50% + ${knobY}px))`;
    const clamp = (v: number, mn: number, mx: number): number => Math.min(mx, Math.max(mn, v));
    this.axis = { x: clamp(dx / max, -1, 1), y: clamp(dy / max, -1, 1) };
  }

  /** Опрашивает текущую позицию стика. */
  poll(): InputState {
    return { axis: this.axis, glowPressed: false };
  }

  /** Отписывается от pointer-событий на base-элементе. */
  dispose(): void {
    this.base.removeEventListener("pointerdown", this.onDown);
    this.base.removeEventListener("pointermove", this.onMove);
    this.base.removeEventListener("pointerup", this.onUp);
    this.base.removeEventListener("pointercancel", this.onUp);
  }
}

/** Кнопка glow на HTMLElement. */
export class GlowButtonInputSource implements InputSource {
  private pressed = false;
  private pointerId: number | null = null;

  private readonly onDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.pressed = true;
    this.pointerId = e.pointerId;
    this.button.setPointerCapture(e.pointerId);
  };

  private readonly onUp = (e: Event): void => {
    if (e instanceof PointerEvent && this.pointerId !== null && e.pointerId !== this.pointerId) {
      return;
    }
    this.pressed = false;
    this.pointerId = null;
  };

  constructor(private readonly button: HTMLElement) {
    this.button.addEventListener("pointerdown", this.onDown);
    this.button.addEventListener("pointerup", this.onUp);
    this.button.addEventListener("pointercancel", this.onUp);
    this.button.addEventListener("lostpointercapture", this.onUp);
  }

  /** Опрашивает текущее нажатие кнопки glow. */
  poll(): InputState {
    return { axis: { x: 0, y: 0 }, glowPressed: this.pressed };
  }

  /** Отписывается от pointer-событий на кнопке. */
  dispose(): void {
    this.button.removeEventListener("pointerdown", this.onDown);
    this.button.removeEventListener("pointerup", this.onUp);
    this.button.removeEventListener("pointercancel", this.onUp);
    this.button.removeEventListener("lostpointercapture", this.onUp);
  }
}

/** Композит — суммирует ось и OR'ит glow от всех источников, нормализует длину. */
export class CompositeInputSource implements InputSource {
  constructor(private readonly sources: InputSource[]) {}

  /** Опрашивает все источники и возвращает нормализованный результат. */
  poll(): InputState {
    let x = 0;
    let y = 0;
    let glow = false;
    for (const s of this.sources) {
      const r = s.poll();
      x += r.axis.x;
      y += r.axis.y;
      glow = glow || r.glowPressed;
    }
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    return { axis: { x, y }, glowPressed: glow };
  }

  /** Вызывает dispose() для всех источников. */
  dispose(): void {
    for (const s of this.sources) s.dispose();
  }
}
